import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AdminClient } from '../../../modules/core/index.js'
import { createPerformanceReviewsAdminApi } from '../adminApi.js'
import { DetailSection, QueryBlock } from '../../../modules/core/index.js'
import { saveBlob } from '../../../modules/core/index.js'
import type { Entitlement } from '../../../modules/add-ons/index.js'
import { PerformanceSummaryModal } from './PerformanceSummaryModal.js'
import { ReceiptList } from './ReceiptList.js'
import { SummaryList } from './SummaryList.js'

const REASON_TEXT: Record<string, string> = {
  enabled: 'on at this facility',
  not_licensed: "not part of the client's agreement",
  license_not_started: 'licensed, but the term has not started',
  license_suspended: 'licensed, but suspended',
  license_ended: 'the license has ended',
  not_activated: 'licensed, but switched off at this facility',
  facility_limit: "licensed, but past the agreement's facility limit",
}

function Standing({ feature }: { feature: Entitlement }) {
  return <p className="mb-2 text-[11px] text-ink-mid">Performance Summaries is {REASON_TEXT[feature.reason] ?? feature.reason}.</p>
}

/** The platform admin's read-only view of a worker's summaries: the list, each opening in a dialog with Print and the CSV. Generates nothing. */
export function AdminWorkerSummaries({ client, clientId, workerId }: { client: AdminClient; clientId: string; workerId: string }) {
  const api = useMemo(() => createPerformanceReviewsAdminApi(client), [client])
  const listQuery = useQuery({ queryKey: ['admin', 'performance-summaries', clientId, workerId], queryFn: () => api.performanceSummaries(clientId, workerId) })
  const [openId, setOpenId] = useState<string | null>(null)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const openQuery = useQuery({
    queryKey: ['admin', 'performance-summary', clientId, workerId, openId],
    queryFn: () => api.performanceSummary(clientId, workerId, openId as string),
    enabled: openId !== null,
    staleTime: Infinity,
  })

  async function download(summaryId: string): Promise<void> {
    const csv = await api.performanceSummaryCsv(clientId, workerId, summaryId)
    saveBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${workerId}-${summaryId}.csv`)
  }

  /** The row's CSV button: one download at a time, a failure shown under the list. */
  function downloadFromRow(summaryId: string): void {
    if (downloading !== null) {
      return
    }
    setDownloading(summaryId)
    setDownloadError(null)
    download(summaryId)
      .catch((error: unknown) => setDownloadError(error instanceof Error ? error.message : 'The download failed.'))
      .finally(() => setDownloading(null))
  }

  return (
    <DetailSection title="Performance summaries" endpoint="…/performance-summaries">
      <QueryBlock query={listQuery} missing="This worker no longer exists.">
        {({ data, feature }) => (
          <>
            <Standing feature={feature} />
            <SummaryList summaries={data} empty="No summary has been generated for this worker." onOpen={setOpenId} onDownload={downloadFromRow} downloading={downloading} />
          </>
        )}
      </QueryBlock>
      {downloadError && (
        <p role="alert" className="mt-2 text-[11px] text-coral">
          {downloadError}
        </p>
      )}
      {openId !== null && openQuery.isError && <p className="mt-2 text-[11px] text-coral">Could not open that summary.</p>}
      {openId !== null && openQuery.data && <PerformanceSummaryModal summary={openQuery.data.data} backLabel="the worker" onDownload={() => download(openId)} onClose={() => setOpenId(null)} />}
    </DetailSection>
  )
}

/** The platform admin's read-only view of a facility's export receipts. */
export function AdminFacilityExports({ client, clientId, facilityId }: { client: AdminClient; clientId: string; facilityId: string }) {
  const api = useMemo(() => createPerformanceReviewsAdminApi(client), [client])
  const receiptsQuery = useQuery({ queryKey: ['admin', 'performance-exports', clientId, facilityId], queryFn: () => api.performanceExports(clientId, facilityId) })
  return (
    <DetailSection title="Performance summary exports" endpoint="…/performance-summaries/exports">
      <QueryBlock query={receiptsQuery} missing="This facility no longer exists.">
        {({ data, feature }) => (
          <>
            <Standing feature={feature} />
            <ReceiptList receipts={data} empty="Nothing has been exported for this facility." />
          </>
        )}
      </QueryBlock>
    </DetailSection>
  )
}
